import React from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from '@components';
import { CreateAgentData } from '../../types';
import { useApiEndpoints } from '../../hooks/useApiEndpoints';
import type { ScenarioDraftController } from '../../hooks/useScenarioDraft';
import { ApiEndpointsSection } from './ApiEndpointsSection';
import { VariableExtractionBuilder } from '../VariableExtractionBuilder';
import { ScenarioSubTab } from './ScenarioSubTab';
import { enabledSystemTools } from '../scenario/systemTools';

export type DataApiSubTab = 'data-extraction' | 'live-api' | 'post-api' | 'ai-tools';

interface DataAndApiTabProps {
  formData: CreateAgentData;
  onInputChange: (
    field: keyof CreateAgentData,
    value: string | number | boolean | string[] | unknown[]
  ) => void;
  errors: Record<string, string>;
  agentId?: string;
  scenario: ScenarioDraftController;
  // Call-Flow-Manager (API Expert Mode) is a tier feature; without it the AI tools sub-tab is hidden.
  callFlowManagerEnabled: boolean;
  activeSubTab: DataApiSubTab;
  onSubTabChange: (key: DataApiSubTab) => void;
}

const DataAndApiTab: React.FC<DataAndApiTabProps> = ({
  formData,
  onInputChange,
  errors,
  agentId,
  scenario,
  callFlowManagerEnabled,
  activeSubTab: requestedSubTab,
  onSubTabChange,
}) => {
  const { t } = useTranslation();
  const activeSubTab =
    requestedSubTab === 'ai-tools' && !callFlowManagerEnabled ? 'data-extraction' : requestedSubTab;

  // Extract variable names from variable_extraction schema for post-call API
  const extractedVariables = React.useMemo(() => {
    if (!formData.variable_extraction) return [];

    try {
      const parsed = JSON.parse(formData.variable_extraction);
      if (parsed.schema?.properties) {
        return Object.keys(parsed.schema.properties);
      }
    } catch (e) {
      // Invalid JSON, return empty array
    }
    return [];
  }, [formData.variable_extraction]);

  // Variables available during live API calls (at call time)
  const liveCallVariables = React.useMemo(
    () => Array.from(new Set(['caller_number', 'agent_name', ...extractedVariables])),
    [extractedVariables]
  );

  // Always include post-call default variables, remove duplicates
  const allAvailableVariables = React.useMemo(
    () =>
      Array.from(
        new Set([
          'caller_number',
          'agent_name',
          'started_at',
          'ended_at',
          'conversation_summary',
          ...extractedVariables,
        ])
      ),
    [extractedVariables]
  );

  const {
    apiEndpoints: liveApiEndpoints,
    updateApiEndpoint: updateLiveApiEndpoint,
    addApiEndpoint: addLiveApiEndpoint,
    removeApiEndpoint: removeLiveApiEndpoint,
    addHeader: addLiveHeader,
    removeHeader: removeLiveHeader,
    updateHeader: updateLiveHeader,
  } = useApiEndpoints({
    formDataCalls: formData.live_api_calls || [],
    callType: 'live',
    onSync: calls => onInputChange('live_api_calls', calls),
  });

  const {
    apiEndpoints: postApiEndpoints,
    updateApiEndpoint: updatePostApiEndpoint,
    addApiEndpoint: addPostApiEndpoint,
    removeApiEndpoint: removePostApiEndpoint,
    addHeader: addPostHeader,
    removeHeader: removePostHeader,
    updateHeader: updatePostHeader,
  } = useApiEndpoints({
    formDataCalls: formData.post_api_calls || [],
    callType: 'post',
    onSync: calls => onInputChange('post_api_calls', calls),
  });

  const subTabs = [
    { key: 'data-extraction' as const, label: t('agents.dataExtractionTab'), icon: 'data_object' },
    { key: 'live-api' as const, label: t('agents.liveApiCallsTab'), icon: 'sync' },
    { key: 'post-api' as const, label: t('agents.postCallApiTab'), icon: 'webhook' },
    ...(callFlowManagerEnabled
      ? [{ key: 'ai-tools' as const, label: t('agents.scenario.tab'), icon: 'smart_toy' }]
      : []),
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('agents.dataAndApiCalls')}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {t('agents.dataAndApiCallsDescription')}
        </p>
      </div>

      {/* Sub-Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8" aria-label="Sub-tabs">
          {subTabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => onSubTabChange(tab.key)}
              className={`
                group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                transition-colors duration-200
                ${
                  activeSubTab === tab.key
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }
              `}
            >
              <span className="material-icons mr-2 text-xl">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="space-y-8">
        {/* Data Extraction Tab */}
        {activeSubTab === 'data-extraction' && (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
                data_object
              </span>
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                {t('agents.dataExtraction')}
              </h4>
            </div>

            <VariableExtractionBuilder
              value={formData.variable_extraction || ''}
              onChange={value => onInputChange('variable_extraction', value)}
              error={errors.variable_extraction}
            />
          </div>
        )}

        {/* Live API Calls Tab */}
        {activeSubTab === 'live-api' && (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
                sync
              </span>
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                {t('agents.liveApiCalls')}
              </h4>
            </div>

            <div>
              <label
                htmlFor="live-api-calls"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('common.status')}
              </label>
              <Select
                id="live-api-calls"
                value={formData.live_api_calls_enabled ? 'enabled' : 'disabled'}
                onChange={value => {
                  const selectedValue = Array.isArray(value) ? value[0] : value;
                  const booleanValue = selectedValue === 'enabled';
                  onInputChange('live_api_calls_enabled', booleanValue);
                }}
                options={[
                  { value: 'disabled', label: t('agents.disabled') },
                  { value: 'enabled', label: t('agents.enabled') },
                ]}
                className="w-full"
                error={errors.live_api_calls_enabled ? true : undefined}
                errorText={errors.live_api_calls_enabled}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('agents.liveApiCallsDescription')}
              </p>
            </div>

            {formData.live_api_calls_enabled && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center mb-4">
                  <span className="material-icons text-primary-500 dark:text-primary-300 mr-2 text-sm">
                    api
                  </span>
                  <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {t('agents.liveApiEndpointsConfiguration')}
                  </h5>
                </div>

                <ApiEndpointsSection
                  apiEndpoints={liveApiEndpoints}
                  updateApiEndpoint={updateLiveApiEndpoint}
                  addApiEndpoint={addLiveApiEndpoint}
                  removeApiEndpoint={removeLiveApiEndpoint}
                  addHeader={addLiveHeader}
                  removeHeader={removeLiveHeader}
                  updateHeader={updateLiveHeader}
                  allAvailableVariables={liveCallVariables}
                  urlPlaceholder="https://api.example.com/live-call"
                  requestBodyPlaceholder={`{\n  "call_id": "{{call_id}}",\n  "customer_id": "{{customer_id}}",\n  "status": "in-progress"\n}`}
                />
              </div>
            )}
          </div>
        )}

        {/* Post Call API Request Tab */}
        {activeSubTab === 'post-api' && (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
                webhook
              </span>
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                {t('agents.postCallApiRequest')}
              </h4>
            </div>

            <div>
              <label
                htmlFor="post-call-api-request"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('common.status')}
              </label>
              <Select
                id="post-call-api-request"
                value={formData.post_call_api_request_enabled ? 'enabled' : 'disabled'}
                onChange={value => {
                  const selectedValue = Array.isArray(value) ? value[0] : value;
                  const booleanValue = selectedValue === 'enabled';
                  onInputChange('post_call_api_request_enabled', booleanValue);
                }}
                options={[
                  { value: 'disabled', label: t('agents.disabled') },
                  { value: 'enabled', label: t('agents.enabled') },
                ]}
                className="w-full"
                error={errors.post_call_api_request_enabled ? true : undefined}
                errorText={errors.post_call_api_request_enabled}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('agents.triggerApiCallsAfterCompletion')}
              </p>
            </div>

            {formData.post_call_api_request_enabled && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center mb-4">
                  <span className="material-icons text-primary-500 dark:text-primary-300 mr-2 text-sm">
                    api
                  </span>
                  <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {t('agents.postCallApiEndpointsConfiguration')}
                  </h5>
                </div>

                <ApiEndpointsSection
                  apiEndpoints={postApiEndpoints}
                  updateApiEndpoint={updatePostApiEndpoint}
                  addApiEndpoint={addPostApiEndpoint}
                  removeApiEndpoint={removePostApiEndpoint}
                  addHeader={addPostHeader}
                  removeHeader={removePostHeader}
                  updateHeader={updatePostHeader}
                  allAvailableVariables={allAvailableVariables}
                  urlPlaceholder="https://api.example.com/post-call"
                  requestBodyPlaceholder={t('agents.requestBodyPlaceholder')}
                  includePatch
                />
              </div>
            )}
          </div>
        )}

        {activeSubTab === 'ai-tools' && (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
                smart_toy
              </span>
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                {t('agents.scenario.heading')}
              </h4>
            </div>
            <ScenarioSubTab
              agentId={agentId}
              scenario={scenario}
              enabledSystemTools={enabledSystemTools(formData)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export { DataAndApiTab };
